import { Card } from 'layouts/Card'
import 'mapbox-gl/dist/mapbox-gl.css'
import { useRef, useState, useEffect, useMemo, useCallback } from 'react'
import mapboxgl from '!mapbox-gl' // eslint-disable-line import/no-webpack-loader-syntax
import { RightOutlined, LeftOutlined, SyncOutlined } from '@ant-design/icons'
import { Tree, Image, message, Tooltip } from 'antd'
import { getPositioningList, getPointTotal, getLayerTreeList, putPositioning } from 'api'
import { useSelector, useDispatch } from 'react-redux'
import { MapSlice, getAuthInfoAction } from 'redux/reducers'
import JSZip from 'jszip'
import { useDeepCompareEffect } from 'ahooks'
import { getDateRange, getHashSearchParam, getStroage } from 'utils'
import { useContextConsumer } from 'context/loginAuthProvider'
import { domToPng } from 'modern-screenshot'
import './MapLargeScreen.scss'
import { changeProjectAction, LocationSlice, getProjectBaseDataAction } from 'redux/reducers'
import { persistor } from 'redux/store'
import MapWorker from 'utils/MapWorker.worker.js'
import { useTranslation } from 'react-i18next'
import { SaveViewIcon } from 'assets/icons'
// ---- deck.gl 集成 ----
import { MapboxOverlay } from '@deck.gl/mapbox'
import { ScatterplotLayer, GeoJsonLayer } from '@deck.gl/layers'
import { throttle } from 'lodash'
import FlowProgress from 'components/Progess/Progess'

mapboxgl.accessToken = 'pk.eyJ1IjoiaG91a3VucGVuZyIsImEiOiJjbGh5bDA0aDAxNTQzM29xYTl6cnJkZmczIn0.-lGSQ3ncYn4V0Uu7b75KCA'
const apiUrl = process.env.REACT_APP_SERVER

// ---------- 颜色字符串 → RGBA 数组 ----------
const parseColorToRGB = colorStr => {
    if (!colorStr) return [0, 0, 0]
    const rgba = colorStr.match(/rgba?\((\d+),\s*(\d+),\s*(\d+)/)
    if (rgba) {
        return [parseInt(rgba[1], 10), parseInt(rgba[2], 10), parseInt(rgba[3], 10)]
    }
    let hex = colorStr.replace('#', '')
    if (hex.length === 3)
        hex = hex
            .split('')
            .map(c => c + c)
            .join('')
    return [parseInt(hex.substring(0, 2), 16), parseInt(hex.substring(2, 4), 16), parseInt(hex.substring(4, 6), 16)]
}

// ---------- 动态半径 ----------
const getRadiusFromZoom = (zoom, type) => {
    if (type === 'shot_circle') {
        return Math.max(1.5, Math.min(2, 1.2 + (zoom - 10) * 0.35))
    }
    return Math.max(1.8, Math.min(3, 1.8 + (zoom - 10) * 0.45))
}

const MapLargeScreen = () => {
    const popup = new mapboxgl.Popup({
        closeButton: false,
        closeOnClick: false,
    })
    const dispatch = useDispatch()
    const mapContainer = useRef(null)
    const mapCard = useRef(null)
    const map = useRef(null)
    const deckOverlay = useRef(null)
    const [lng, setLng] = useState()
    const [lat, setLat] = useState()
    const [zoom, setZoom] = useState(10)
    const [closeSider, setCloseSider] = useState(true)
    const [mapStyleReady, setMapStyleReady] = useState(false)
    const [currentViewport, setCurrentViewport] = useState()
    const rangePicker = useSelector(state => state.MapSlice.rangePicker)
    const todayRef = useRef(rangePicker)
    const [currentMapStyle, setCurrentMapStyle] = useState('mapbox://styles/mapbox/streets-v12')
    const project = useSelector(state => state.HeaderSlice.userInfo)?.project
    const [positioningList, setPositioningList] = useState()
    const [screenShotBtnLoading, setScreenShotBtnLoading] = useState(false)
    const [allPointTotal, setAllPointTotal] = useState()
    const userInfo = useSelector(state => state.HeaderSlice.userInfo)
    const projectId = userInfo?.user?.projectId
    const { t } = useTranslation()
    const projectBaseData = useSelector(state => state.HeaderSlice.projectBaseData)
    const receiverTypeList = projectBaseData?.data?.receiverType
    const { loginLargeScreenMap, token } = useContextConsumer()

    // ---- deck.gl 图层数据 ----
    const [layersData, setLayersData] = useState({})
    const layersDataRef = useRef(layersData)
    useEffect(() => {
        layersDataRef.current = layersData
    }, [layersData])

    const loadingCountRef = useRef(0)
    const loadingUrls = useRef(new Set())

    /* 获取项目信息 */
    useEffect(() => {
        if (!projectId) return
        dispatch(getProjectBaseDataAction(projectId))
    }, [projectId, dispatch])

    const noneStr = 'mapbox://styles/houkunpeng/cll61dfb400lm01pdf0j8hp8e/draft'

    // 截图
    useEffect(() => {
        if (!screenShotBtnLoading) return
        const exportPicture = (link, name = 'name') => {
            const file = document.createElement('a')
            file.style.display = 'none'
            file.href = link
            file.download = decodeURI(name)
            document.body.appendChild(file)
            file.click()
            document.body.removeChild(file)
        }
        domToPng(document.getElementsByClassName('map')[0]).then(dataUrl => {
            setScreenShotBtnLoading(false)
            exportPicture(dataUrl, `${rangePicker[0]}---${rangePicker[1]}`)
            document.getElementsByClassName('sidebar-right')[0].style.display = 'block'
        })
    }, [screenShotBtnLoading, closeSider])

    useEffect(() => {
        if (!project || rangePicker) return
        const range = getDateRange(project)
        dispatch(MapSlice.actions.changeRangePicker([range.startDate, range.endDate]))
        todayRef.current = [range.startDate, range.endDate]
    }, [project])

    useEffect(() => {
        if (!positioningList) return
        const obj = positioningList[0]
        setCurrentViewport(
            JSON.stringify({
                latitude: obj.latitude,
                longitude: obj.longitude,
                zoom: obj.zoom,
                id: obj.id,
            })
        )
        setLng(obj.longitude.toFixed(4))
        setLat(obj.latitude.toFixed(4))
        setZoom(obj.zoom)
    }, [positioningList])

    useEffect(
        () =>
            getPositioningList().then(res => {
                if (!token) return
                setPositioningList(res?.data?.data)
            }),
        [token]
    )

    useEffect(() => {
        if (!map.current) return
        const handleMove = throttle(() => {
            setLng(map.current?.getCenter().lng.toFixed(4))
            setLat(map.current?.getCenter().lat.toFixed(4))
            setZoom(map.current?.getZoom().toFixed(3))
        }, 100)
        map.current?.on('move', handleMove)
        return () => {
            map.current?.off('move', handleMove)
            handleMove.cancel()
        }
    })

    useEffect(async () => {
        if (!project) return
        const AllPointTotal = await getPointTotal(project?.id)
        setAllPointTotal(AllPointTotal)
    }, [project?.id])

    // 地图初始化 + deck.gl overlay
    useEffect(() => {
        if (map.current) return
        if (!lng || !lat) return
        if (!mapContainer.current) return

        map.current = new mapboxgl.Map({
            container: mapContainer.current,
            style: currentMapStyle,
            center: [lng, lat],
            zoom: zoom,
            antialias: false,
            preserveDrawingBuffer: true,
        })
        map.current?.addControl(new mapboxgl.NavigationControl())
        const geolocate = new mapboxgl.GeolocateControl({
            geolocation: {
                getCurrentPosition: () => map.current?.setCenter([lng, lat]),
                watchPosition: () => {},
                clearWatch: () => {},
            },
        })
        map.current?.addControl(geolocate)
        map.current?.addControl(new mapboxgl.ScaleControl({ maxWidth: 80 }))
        map.current?.on('style.load', () => setMapStyleReady(true))

        deckOverlay.current = new MapboxOverlay({
            layers: [],
            onClick: async info => {
                const layerId = info.layer?.id
                if (!layerId) {
                    popup.remove()
                    return
                }
                // 边界线/几何线图层 (GeoJsonLayer)
                if (layerId.startsWith('geojson-')) {
                    if (info.object) {
                        const props = info.object.properties || {}
                        const html = Object.entries(props)
                            .filter(([k]) => k !== 'points' && k !== 'properties' && k !== 'status')
                            .map(([k, v]) => `<div class='popupItem'><span>${k}</span><span>${v}</span></div>`)
                            .join('')
                        popup.setLngLat(info.coordinate).setHTML(`<div>${html}</div>`).addTo(map.current)
                    } else {
                        popup.remove()
                    }
                    return
                }
                // 点图层 (ScatterplotLayer)
                if (layerId.startsWith('scatter-') && info.index >= 0) {
                    const layerName = layerId.replace('scatter-', '')
                    const binData = layersDataRef.current[layerName]
                    if (!binData || binData.type !== 'pointBinary') return
                    const bin = binData.data
                    const pointIndex = info.index

                    popup.setLngLat(info.coordinate).setHTML('<div>Loading...</div>').addTo(map.current)

                    try {
                        const props = await loadPropertiesFromDB(
                            pointIndex,
                            bin.dailyOffsets,
                            bin.dailyDates,
                            bin.storeName,
                            bin.projectId
                        )
                        const fieldsOrder = ['designX', 'designY', 'line', 'shotType', 'latitude', 'point', 'longitude']
                        const html = fieldsOrder
                            .map(key => {
                                const value = props[key] != null ? props[key] : ''
                                return `<div class='popupItem'><span>${key}</span><span>${value}</span></div>`
                            })
                            .join('')
                        popup.setHTML(`<div>${html}</div>`)
                    } catch (err) {
                        popup.remove()
                    }
                } else {
                    popup.remove()
                }
            },
        })
        map.current.addControl(deckOverlay.current)
    })

    useEffect(() => {
        return () => {
            map.current?.remove()
            map.current = null
            mapContainer.current = null
            backupDataList.current = null
        }
    }, [])

    /* *********************************************************************************** */
    const [treeConfig, setTreeConfig] = useState([])
    const [layerListConfig, setLayerListConfig] = useState([])
    const [expandedKeys, setExpandedKeys] = useState([])
    const [perRender, setPerRender] = useState(false)
    const backupDataList = useRef([])
    const renderLayers = useSelector(state => state.MapSlice.renderLayers)
    const [dataLoading, setDataLoading] = useState(false)

    const [loadPercent, setLoadPercent] = useState(0)
    // 与其他 useState 放在一起
    const [geometryLoading, setGeometryLoading] = useState(false)
    const [geometryPercent, setGeometryPercent] = useState(0)
    // 观测系统下载字节累计
    const geometryDownloadedRef = useRef(0)
    const [downloadedSizeText, setDownloadedSizeText] = useState('0 KB')
    const [dataTooltipText, setDataTooltipText] = useState('')
    const [geometryTooltipText, setGeometryTooltipText] = useState('')
    const language = getStroage('language')

    const renderLayersRef = useRef(renderLayers)
    useEffect(() => {
        renderLayersRef.current = renderLayers
    }, [renderLayers])

    const zoomRef = useRef(zoom)
    useEffect(() => {
        zoomRef.current = zoom
    }, [zoom])

    const project_id = getHashSearchParam('project_id')
    useEffect(() => {
        if (!token) loginLargeScreenMap({ username: 'bgpmap@klseis.com', password: '.7;A|#VlA)JH' })
    }, [])

    useEffect(() => {
        if (token) {
            dispatch(getAuthInfoAction())
        }
    }, [token])

    useEffect(async () => {
        if (project_id) {
            await dispatch(changeProjectAction(project_id))
            await persistor.purge()
            dispatch(LocationSlice.actions.clearLocation())
            const origin = window.location.origin
            window.location.replace(origin + '/megalinkMap')
        }
    }, [project_id])

    // 工具函数
    const decompressionSPSFile = async res => {
        try {
            const new_zip = new JSZip()
            const zip = await new_zip.loadAsync(res.data)
            for (let p in zip.files) {
                if (!zip.files[p].dir && /\.(json)$/.test(zip.files[p].name)) {
                    let content = await zip.file(zip.files[p].name).async('string')
                    return JSON.parse(content)
                }
            }
        } catch (error) {
            message.warning('sps文件解压失败')
        }
    }

    const backupData = (layerSourceName, data, dataUrl) => {
        const index = backupDataList.current.findIndex(item => item.title === layerSourceName)
        if (index >= 0) {
            backupDataList.current.splice(index, 1, { title: layerSourceName, data, dataUrl })
        } else {
            backupDataList.current.push({ title: layerSourceName, data, dataUrl })
        }
    }

    const toBinary = points => {
        const len = points.length
        const coords = new Float32Array(len * 2)
        // 观测系统不需要索引，但保留空数组以保持结构统一
        const idxs = new Uint32Array(len)
        for (let i = 0; i < len; i++) {
            const p = points[i]
            coords[i * 2] = p.coordinates[0] ?? 0
            coords[i * 2 + 1] = p.coordinates[1] ?? 0
            idxs[i] = i
        }
        return { coords, idxs, len }
    }

    const totalGeometrySizeRef = useRef(0) // 用于累加所有文件的总大小

    // 加载 Geometry 数据的辅助函数
    const loadGeometryData = async (filePath, layerSourceName) => {
        if (loadingUrls.current.has(filePath)) return
        loadingUrls.current.add(filePath)
        loadingCountRef.current += 1
        setGeometryLoading(true)
        setGeometryPercent(0)
        geometryDownloadedRef.current = 0
        setGeometryTooltipText('正在获取文件大小…')

        try {
            const baseUrl = filePath.replace(/\.gjz$/i, '')
            const suffixes = ['_point.gjz', '_line.gjz', '.gjz']

            const fileList = [
                {
                    type: 'preplot_',
                    fileName: `${baseUrl.split('/').pop()}.gjz`,
                },
            ]

            // 获取文件大小并累加
            try {
                const sizeRes = await fetch(`${apiUrl}/map/file/getMapFileLoadInfo`, {
                    method: 'POST',
                    headers: {
                        'Content-Type': 'application/json',
                        Authorization: 'Bearer ' + token,
                    },
                    body: JSON.stringify({ projectId: project?.id, fileList }),
                })
                if (sizeRes.ok) {
                    const data = await sizeRes.json()
                    const fileSize = Number(data?.data?.fileSize) || 0
                    totalGeometrySizeRef.current += fileSize
                }
            } catch (e) {
                // 获取大小失败时稍后按实际下载量显示
            }

            let result = null
            let matchedSuffix = ''
            for (const suffix of suffixes) {
                const url = filePath.startsWith('http') ? `${baseUrl}${suffix}` : `${apiUrl}${baseUrl}${suffix}`
                try {
                    const resp = await fetch(url, {
                        headers: { Authorization: 'Bearer ' + token },
                    })
                    if (resp.ok) {
                        const arrayBuffer = await resp.arrayBuffer()
                        const byteLength = arrayBuffer.byteLength
                        geometryDownloadedRef.current += byteLength

                        const total = totalGeometrySizeRef.current
                        const percent =
                            total <= 0 ? 0 : Math.min(99, Math.round((geometryDownloadedRef.current / total) * 100))
                        setGeometryPercent(percent)

                        // 更新 Tooltip 文本
                        const loadedKB = (geometryDownloadedRef.current / 1024).toFixed(1)
                        if (total > 0) {
                            const totalKB = (total / 1024).toFixed(1)
                            setGeometryTooltipText(
                                language === 'zh'
                                    ? `加载进度：已加载 ${loadedKB} KB，总 ${totalKB} KB`
                                    : `Loading: ${loadedKB} KB / Total: ${totalKB} KB`
                            )
                        } else {
                            setGeometryTooltipText(
                                language === 'zh'
                                    ? `加载进度：已加载 ${loadedKB} KB（总大小计算中…）`
                                    : `Loading: ${loadedKB} KB (Calculating total size...)`
                            )
                        }

                        result = await decompressionSPSFile({ data: arrayBuffer })
                        matchedSuffix = suffix
                        if (result) {
                            break
                        }
                    }
                } catch (err) {
                    // 尝试下一个后缀
                }
            }

            if (!result) throw new Error('未找到任何可用的 Geometry 文件')

            const features = result?.features || []

            if (
                matchedSuffix === '_point.gjz' ||
                (matchedSuffix === '.gjz' && features.length > 0 && features[0].geometry.type === 'Point')
            ) {
                const points = features.map(f => ({
                    coordinates: f.geometry.coordinates,
                    properties: f.properties || {},
                }))
                const bin = toBinary(points)

                setLayersData(prev => ({
                    ...prev,
                    [layerSourceName]: {
                        type: 'pointBinary',
                        data: {
                            coords: bin.coords,
                            dailyOffsets: [0, bin.len],
                            dailyDates: [layerSourceName],
                            storeName: '',
                            projectId: project?.id,
                            length: bin.len,
                            properties: points.map(p => p.properties),
                        },
                    },
                }))
            } else {
                setLayersData(prev => ({
                    ...prev,
                    [layerSourceName]: {
                        type: 'line',
                        data: features,
                    },
                }))
            }

            backupData(layerSourceName, result)
        } catch (err) {
            message.error('Geometry 数据加载失败')
        } finally {
            loadingUrls.current.delete(filePath)
            loadingCountRef.current -= 1
            if (loadingCountRef.current === 0) {
                setGeometryLoading(false)
                setGeometryPercent(100)
                setGeometryTooltipText('几何数据加载完成')
            }
        }
    }

    const loadPropertiesFromDB = (pointIndex, dailyOffsets, dailyDates, storeName, projectId) => {
        return new Promise(resolve => {
            let dayIdx = 0
            for (let i = 0; i < dailyOffsets.length - 1; i++) {
                if (pointIndex >= dailyOffsets[i] && pointIndex < dailyOffsets[i + 1]) {
                    dayIdx = i
                    break
                }
            }
            const localIndex = pointIndex - dailyOffsets[dayIdx]
            const dateStr = dailyDates[dayIdx]
            const request = indexedDB.open(String(projectId), 1)
            request.onsuccess = e => {
                const db = e.target.result
                const tx = db.transaction(storeName, 'readonly')
                const store = tx.objectStore(storeName)
                const getReq = store.get(dateStr)
                getReq.onsuccess = () => {
                    const features = getReq.result?.geoJson?.features
                    if (features && localIndex < features.length) {
                        resolve(features[localIndex].properties || {})
                    } else {
                        resolve({})
                    }
                }
                getReq.onerror = () => resolve({})
            }
            request.onerror = () => resolve({})
        })
    }

    // 树勾选：仅更新 Redux
    const onCheckTree = (checkedKeys, e, boolean) => {
        if (e.checked || boolean) {
            let list = checkedKeys.filter(
                item => item !== 'Boundaries' && item !== 'Geometry' && item !== 'Production' && item !== 'Obstacles'
            )
            dispatch(MapSlice.actions.changeRenderLayers(list))
        } else {
            let copy = [...renderLayers]
            const handleFun = (node, arr) => {
                const idx = arr.findIndex(item => item === node.key)
                if (idx !== -1) arr.splice(idx, 1)
            }
            if (e.node.children) {
                e.node.children.forEach(c => handleFun(c, copy))
            } else {
                handleFun(e.node, copy)
            }
            dispatch(MapSlice.actions.changeRenderLayers(copy))
        }
    }

    // Worker 数据处理
    const renderLayerTrueData = (data, type) => {
        const layerSourceName = type

        if (data && data.binary) {
            const coords = new Float32Array(data.buffer)
            setLayersData(prev => ({
                ...prev,
                [layerSourceName]: {
                    type: 'pointBinary',
                    data: {
                        coords,
                        properties: data.properties,
                        length: data.length,
                    },
                },
            }))
            if (layerSourceName === 'Recorded') {
                setLayersData(prev => ({
                    ...prev,
                    RecordedPoint: {
                        type: 'pointBinary',
                        data: { coords, properties: data.properties, length: data.length },
                    },
                }))
            }
            return
        }

        if (type === 'boundariesData') {
            const name = data.name
            backupData(name, data.geojson)
            setLayersData(prev => ({
                ...prev,
                [name]: { type: 'line', data: data.geojson.features },
            }))
            return
        }

        // ====================== 障碍物 ======================
        if (type === 'obstaclesData') {
            const name = data.name
            backupData(name, data.geojson)
            setLayersData(prev => ({
                ...prev,
                [name]: { type: 'line', data: data.geojson.features }, // 直接存储 features
            }))
            return
        }

        if (type === 'geometryData') {
            const name = data.fileName
            backupData(name, [], data.filePath)
            return
        }

        const features = data?.features || []
        if (features.length > 0) {
            const points = features.map(f => ({
                coordinates: f.geometry?.coordinates || [0, 0],
                properties: f.properties || {},
            }))
            setLayersData(prev => ({
                ...prev,
                [layerSourceName]: { type: 'point', data: points },
            }))
            if (layerSourceName === 'Recorded') {
                setLayersData(prev => ({
                    ...prev,
                    RecordedPoint: { type: 'point', data: points },
                }))
            }
        }
    }

    const binaryAccumulators = useRef({}) // 点二进制图层累积

    // ========= 新增：rAF 分批渲染相关 =========
    const pendingUpdateRef = useRef(false)
    const rafIdRef = useRef(null)

    /**
     * 将当前 binaryAccumulators 中的数据合并到 layersData，
     * 并保证每次最多一帧执行一次，避免 React 自动批处理吞掉中间状态。
     */
    const scheduleLayerUpdate = useCallback(() => {
        if (pendingUpdateRef.current) return // 已有待处理帧
        pendingUpdateRef.current = true
        rafIdRef.current = requestAnimationFrame(() => {
            // 合并所有累积器的当前快照，一次 setState 更新多个图层
            setLayersData(prev => {
                const next = { ...prev }
                const accs = binaryAccumulators.current
                for (const [type, acc] of Object.entries(accs)) {
                    next[type] = {
                        type: 'pointBinary',
                        data: {
                            coords: acc.coords,
                            dailyOffsets: acc.dailyOffsets,
                            dailyDates: acc.dailyDates,
                            storeName: acc.storeName,
                            projectId: project?.id,
                            length: acc.offset,
                        },
                    }
                    // 同步更新 RecordedPoint
                    if (type === 'Recorded') {
                        next.RecordedPoint = { ...next[type] }
                    }
                }
                // 线图层（Streamer）也可在此累积，但为了保持清晰，可单独处理，
                // 这里只处理二进制点图层，线图层仍需独立 setState（见下）。
                return next
            })
            pendingUpdateRef.current = false
        })
    }, [project?.id])

    // Worker 加载数据
    useEffect(() => {
        if (!rangePicker || !project?.id || !perRender || !MapWorker) return
        setLayersData({})
        backupDataList.current = []
        setDataLoading(true)
        const worker = new MapWorker()
        worker.postMessage({
            projectId: project?.id,
            token,
            apiUrl,
            startDate: rangePicker[0],
            endDate: rangePicker[1],
            receiverTypeList,
        })
        worker.onmessage = e => {
            const { type } = e.data

            // 进度
            if (e.data.type === 'progress') {
                setLoadPercent(e.data.percent)

                const dlKB = Math.round(e.data.downloadedBytes / 1024)
                const dlMB = (dlKB / 1024).toFixed(1)
                const loadedText = dlKB > 1024 ? `${dlMB} MB` : `${dlKB} KB`
                setDownloadedSizeText(loadedText)

                const totalBytes = e.data.totalBytes || 0
                let tooltipText
                if (totalBytes > 0) {
                    const totKB = Math.round(totalBytes / 1024)
                    const totMB = (totKB / 1024).toFixed(1)
                    const totalText = totKB > 1024 ? `${totMB} MB` : `${totKB} KB`
                    tooltipText =
                        language === 'zh'
                            ? `加载进度：已加载 ${loadedText} / ${totalText}`
                            : `Loading progress: ${loadedText} / ${totalText}`
                } else {
                    tooltipText =
                        language === 'zh'
                            ? `加载进度：已加载 ${loadedText}，总大小计算中…`
                            : `Loading progress: ${loadedText}, calculating total size...`
                }
                setDataTooltipText(tooltipText)
                return
            }

            // 1. 处理结束信号（提前返回）
            if (type === 'workerDone') {
                setDataLoading(false)
                // 取消可能等待中的 rAF
                if (rafIdRef.current) {
                    cancelAnimationFrame(rafIdRef.current)
                    rafIdRef.current = null
                }
                // 最后一次强制刷新，确保数据完整
                if (pendingUpdateRef.current) {
                    // 如果还有未渲染的累积，立即执行一次
                    pendingUpdateRef.current = false
                    // 直接调用一次 scheduleLayerUpdate 内部逻辑
                    setLayersData(prev => {
                        const next = { ...prev }
                        const accs = binaryAccumulators.current
                        for (const [type, acc] of Object.entries(accs)) {
                            next[type] = {
                                type: 'pointBinary',
                                data: {
                                    coords: acc.coords,
                                    dailyOffsets: acc.dailyOffsets,
                                    dailyDates: acc.dailyDates,
                                    storeName: acc.storeName,
                                    projectId: project?.id,
                                    length: acc.offset,
                                },
                            }
                            if (type === 'Recorded') {
                                next.RecordedPoint = { ...next[type] }
                            }
                        }
                        return next
                    })
                }
                // 关闭 loading
                setTimeout(() => {
                    if (loadingCountRef.current === 0) setDataLoading(false)
                }, 150)
                // 清空累积器（组件卸载时也会回收）
                binaryAccumulators.current = {}
                return
            }

            // 2. 边界、Geometry、Streamer 等特殊处理
            if (type === 'boundariesData') {
                renderLayerTrueData(e.data.data, type)
                return
            }

            if (type === 'obstaclesData') {
                renderLayerTrueData(e.data.data, type)
                return
            }

            if (type === 'geometryData') {
                const name = e.data.data.fileName
                backupData(name, [], e.data.data.filePath)
                if (renderLayersRef.current?.includes(name)) {
                    loadGeometryData(e.data.data.filePath, name)
                }
                return
            }

            // 3. 二进制点数据（总图层 + 当日图层）
            if ('buffer' in e.data || e.data.len === 0) {
                const { type, len, dailyOffsets, dailyDates, storeName } = e.data
                const isTotal = !type.startsWith('Daily ')

                if (isTotal) {
                    // ---------- 总图层：缓存到累积器，不立即渲染 ----------
                    if (!binaryAccumulators.current[type]) {
                        binaryAccumulators.current[type] = {
                            coords: new Float32Array(len * 2 || 0),
                            offset: 0,
                            dailyOffsets: [0],
                            dailyDates: [],
                            storeName,
                        }
                    }
                    const acc = binaryAccumulators.current[type]

                    if (len > 0) {
                        const requiredSize = (acc.offset + len) * 2
                        if (acc.coords.length < requiredSize) {
                            const newCapacity = Math.max(requiredSize, acc.coords.length * 1.2) | 0
                            const newCoords = new Float32Array(newCapacity)
                            newCoords.set(acc.coords.subarray(0, acc.offset * 2))
                            acc.coords = newCoords
                        }
                        const dayCoords = new Float32Array(e.data.buffer)
                        acc.coords.set(dayCoords, acc.offset * 2)
                        acc.offset += len
                    }
                    acc.dailyOffsets.push(acc.offset)
                    acc.dailyDates.push(dailyDates[0])

                    // 请求一帧后渲染（合并当天数据）
                    scheduleLayerUpdate()
                } else {
                    // ---------- 当日图层：直接覆盖（单天），也走一次帧调度以保持流畅 ----------
                    const coords = e.data.buffer ? new Float32Array(e.data.buffer) : new Float32Array(0)
                    // 为了也能逐步出现，也使用 rAF 调度
                    // 但当日图层不需要累积，我们可以临时存到一个特殊字段，或者直接每次更新时覆盖。
                    // 简单做法：依然使用一个临时累积器（但每次覆盖），然后统一用 scheduleLayerUpdate。
                    if (!binaryAccumulators.current[type]) {
                        binaryAccumulators.current[type] = {
                            coords: new Float32Array(0),
                            offset: 0,
                            dailyOffsets: [0],
                            dailyDates: [],
                            storeName,
                        }
                    }
                    const acc = binaryAccumulators.current[type]
                    acc.coords = coords
                    acc.offset = coords.length / 2
                    acc.dailyOffsets = dailyOffsets || [0, acc.offset]
                    acc.dailyDates = dailyDates || []
                    acc.storeName = storeName

                    scheduleLayerUpdate()
                }
                return
            }

            // 4. 兜底老格式
            renderLayerTrueData(e.data.data, type)
        }
        return () => worker.terminate()
    }, [rangePicker, project, perRender, MapWorker])

    useEffect(() => {
        if (treeConfig.length > 0) setExpandedKeys(['Boundaries', 'Obstacles', 'Geometry', 'Production'])
    }, [treeConfig])

    useDeepCompareEffect(() => {
        if (layerListConfig.length === 0) return
        if (renderLayers) {
            dispatch(MapSlice.actions.changeRenderLayers(renderLayers))
        } else {
            if (project?.projectStatus === '29-1') {
                const layers = layerListConfig?.map(item => item.layerSoruceName)
                dispatch(MapSlice.actions.changeRenderLayers(layers))
            } else {
                const layers = layerListConfig
                    ?.filter(item => !item.layerGeometryType)
                    ?.map(item => item.layerSoruceName)
                dispatch(MapSlice.actions.changeRenderLayers(layers))
            }
        }
    }, [layerListConfig, receiverTypeList])

    // 构建 deck.gl layers
    const deckLayers = useMemo(() => {
        if (!layerListConfig.length || !mapStyleReady) return []

        return layerListConfig
            .filter(item => renderLayers?.includes(item.layerSoruceName) && item.layerSoruceName !== 'RecordedPoint')
            .map(config => {
                const layerData = layersData[config.layerSoruceName]

                if (config.type === 'Obstacles') {
                    const fillColor = config.fillColor || config.layerColor
                    const borderColor = config.borderColor || config.layerColor

                    return new GeoJsonLayer({
                        id: `geojson-${config.layerSoruceName}`,
                        data: layerData?.data,
                        pickable: true,
                        stroked: true,
                        filled: false,
                        filled: config?.showFill === 1,
                        stroked: config?.showBorder === 1,
                        getFillColor: parseColorToRGB(fillColor),
                        getLineColor: parseColorToRGB(borderColor),
                        lineWidthScale: 1,
                        lineWidthMinPixels: 2,
                        parameters: { depthTest: false },
                    })
                }

                // Geometry 图层按需加载
                if (config.type === 'Geometry') {
                    if (!layerData || layerData.data.length === 0) {
                        const backupItem = backupDataList.current.find(b => b.title === config.layerSoruceName)
                        if (backupItem?.dataUrl) {
                            loadGeometryData(backupItem.dataUrl, config.layerSoruceName)
                        }
                        return null
                    }
                    if (layerData.type === 'line') {
                        return new GeoJsonLayer({
                            id: `geojson-${config.layerSoruceName}`,
                            data: layerData.data,
                            pickable: true,
                            stroked: true,
                            filled: false,
                            getLineColor: parseColorToRGB(config.layerColor),
                            lineWidthScale: 1,
                            lineWidthMinPixels: 2,
                            parameters: { depthTest: false },
                        })
                    } else if (layerData.type === 'pointBinary') {
                        const bin = layerData.data
                        const data = {
                            length: bin.length,
                            attributes: {
                                getPosition: { value: bin.coords, size: 2 },
                            },
                        }
                        return new ScatterplotLayer({
                            id: `scatter-${config.layerSoruceName}`,
                            data,
                            pickable: true,
                            opacity: config.opacity ?? 1,
                            stroked: false,
                            filled: true,
                            radiusUnits: 'meters',
                            radiusMinPixels: 1,
                            radiusMaxPixels: 5,
                            getFillColor: parseColorToRGB(config.layerColor),
                            getRadius: () => getRadiusFromZoom(zoomRef.current, config.layerRenderType),
                            updateTriggers: { getRadius: [zoom, config.layerRenderType] },
                            parameters: { depthTest: false },
                        })
                    }
                    return null
                }

                const colorStreamerRGB = parseColorToRGB(config.layerColor)
                if (
                    (config.layerSoruceName === 'Recorded(Streamer)' ||
                        config.layerSoruceName === 'Daily Recorded(Streamer)') &&
                    receiverTypeList?.includes('streamer') &&
                    layerData?.type === 'line'
                ) {
                    return new GeoJsonLayer({
                        id: `geojson-${config.layerSoruceName}`,
                        data: layerData.data,
                        pickable: true,
                        opacity: 1,
                        stroked: true,
                        filled: false,
                        getLineColor: colorStreamerRGB,
                        lineWidthScale: 2,
                        lineWidthMinPixels: 4,
                        lineWidthMaxPixels: 8,
                        lineJointRounded: true,
                        lineCapRounded: true,
                        lineWidthUnits: 'pixels',
                        parameters: { depthTest: false, blend: true },
                    })
                }

                if (!layerData || layerData.data.length === 0) return null

                const colorRGB = parseColorToRGB(config.layerColor)
                const opacity = config.opacity ?? 1
                const isLine = config.type === 'Boundaries'

                if (isLine) {
                    return new GeoJsonLayer({
                        id: `geojson-${config.layerSoruceName}`,
                        data: layerData.data,
                        pickable: true,
                        opacity: 1,
                        stroked: true,
                        filled: false,
                        getLineColor: colorRGB,
                        lineWidthScale: 1,
                        lineWidthMinPixels: 2,
                        parameters: { depthTest: false },
                    })
                }

                // Production 点图层
                const isBinary = layerData.type === 'pointBinary'
                if (isBinary) {
                    const bin = layerData.data
                    const data = {
                        length: bin.length,
                        attributes: {
                            getPosition: { value: bin.coords, size: 2 },
                        },
                    }
                    return new ScatterplotLayer({
                        id: `scatter-${config.layerSoruceName}`,
                        data,
                        pickable: true,
                        opacity,
                        stroked: false,
                        filled: true,
                        radiusUnits: 'meters',
                        radiusMinPixels: 1,
                        radiusMaxPixels: 5,
                        getFillColor: colorRGB,
                        getRadius: () => getRadiusFromZoom(zoomRef.current, config.layerRenderType),
                        updateTriggers: { getRadius: [zoom, config.layerRenderType] },
                        parameters: { depthTest: false },
                    })
                }
            })
            .filter(Boolean)
    }, [layerListConfig, renderLayers, layersData, mapStyleReady])

    useEffect(() => {
        if (deckOverlay.current) {
            deckOverlay.current.setProps({ layers: deckLayers })
        }
    }, [deckLayers])

    // 预渲染完成标志
    useEffect(() => {
        if (layerListConfig.length === 0 || !mapStyleReady) return
        setPerRender(true)
    }, [layerListConfig, mapStyleReady])

    const getPointOrLine = (key, name) => {
        let PointTotal =
            allPointTotal?.data?.data?.designSourcePointTotal + allPointTotal?.data?.data?.designReceiverPointTotal
        let layerRenderType = 'line'

        if (name !== 'Production') return layerRenderType
        if (PointTotal < 300000) {
            layerRenderType = 'circle'
        }
        if (PointTotal > 300000 && allPointTotal?.data?.data?.designReceiverPointTotal < 300000) {
            layerRenderType = 'circle'
        }
        if (
            PointTotal > 300000 &&
            (key === 'Recorded' || key === 'Daily Recorded') &&
            allPointTotal?.data?.data?.designReceiverPointTotal < 300000
        ) {
            layerRenderType = 'line'
        }
        if (PointTotal > 300000 && allPointTotal?.data?.data?.designReceiverPointTotal > 300000) {
            layerRenderType = 'line'
        }
        if ((layerRenderType === 'circle' && key === 'Recorded') || key === 'Daily Recorded') {
            layerRenderType = 'shot_circle'
        }
        if (key === 'Recorded(Streamer)' || key === 'Daily Recorded(Streamer)') {
            layerRenderType = 'line'
        }
        if (key === 'Daily Deployment' || key === 'Daily Retrieval') {
            layerRenderType = 'shot_circle'
        }
        return layerRenderType
    }

    const getTreeConfig = (GroupType, nameValue, productionType, color) => {
        const getProductionTypeColor = productionType => {
            switch (productionType) {
                case 'Deployed':
                    return '#84a729'
                case 'Retrieval':
                    return '#000eff'
                case 'Recorded':
                    return 'rgba(255, 24, 0, 0.7)'
                case 'Daily Deployment':
                    return '#e6e001'
                case 'Daily Retrieval':
                    return '#010000'
                case 'Daily Recorded':
                    return '#e17aff'
                case 'Recorded(Streamer)':
                    return '#ff0000'
                case 'Daily Recorded(Streamer)':
                    return '#ffff00'
                default:
                    return '#000'
            }
        }
        if (GroupType === 'Boundaries') {
            switch (nameValue) {
                case 'layerColor':
                    return color
                case 'legendBoundaries':
                    return true
                case 'disableLegend':
                    return false
            }
        } else if (GroupType === 'Obstacles') {
            // 新增障碍物分支
            switch (nameValue) {
                case 'layerColor':
                    return color
                case 'legendObstacles':
                    return true
                case 'disableLegend':
                    return false
                default:
                    return undefined
            }
        } else if (GroupType === 'Geometry') {
            switch (nameValue) {
                case 'layerColor':
                    return productionType.split('.')[1].toLowerCase() === 's' ? '#bfbfbf' : '#b5caff'
                case 'legendBoundaries':
                    return false
                case 'disableLegend':
                    return true
            }
        } else if (GroupType === 'Production') {
            switch (nameValue) {
                case 'layerColor':
                    return getProductionTypeColor(productionType)
                case 'legendBoundaries':
                    return false
                case 'disableLegend':
                    return false
            }
        }
    }

    useEffect(() => {
        if (!project?.id || !allPointTotal) return
        getLayerTreeList(project.id).then(res => {
            const apiData = res?.data?.data
            if (!apiData) return message.warning('common.tips28')
            const layerListData = [...apiData.slice(1), apiData[0]]
            const obsIdx = layerListData.findIndex(item => item.name === 'Obstacles')
            if (obsIdx !== -1) {
                const [obstacles] = layerListData.splice(obsIdx, 1)
                layerListData.splice(layerListData.length - 1, 0, obstacles)
            }
            let treeConfig = []
            let layerListConfig = []
            let layerNameCount = 0
            apiData.forEach(fatherItem => {
                const treeObj = {
                    title: <span className='layerGroup'>{fatherItem.name}</span>,
                    key: fatherItem.name,
                    children: [],
                }
                fatherItem.layerList.forEach(sunItem => {
                    treeObj.children.push({
                        title: <span className='layerItem'>{sunItem.name}</span>,
                        key: sunItem.name,
                        isLeaf: true,
                        color: sunItem.color,
                        selectable: true,
                    })
                })
                treeConfig.push(treeObj)
            })
            layerListData.forEach(fatherItem => {
                fatherItem.layerList.forEach(sunItem => {
                    layerListConfig.push({
                        layerRenderType: getPointOrLine(sunItem.name, fatherItem.name),
                        layerColor: getTreeConfig(fatherItem.name, 'layerColor', sunItem.name, sunItem.color),
                        layerName: layerNameCount,
                        layerSoruceName: sunItem.name,
                        layerGeometryType: fatherItem.name === 'Geometry' ? true : false,
                        legendName: sunItem.name,
                        legendBoundaries: fatherItem.name === 'Boundaries' ? true : false,
                        legendObstacles: fatherItem.name === 'Obstacles' ? true : false,
                        disableLegend: fatherItem.name === 'Geometry' ? true : false,
                        opacity: /\.(S|s)$/.test(sunItem.name) ? 0.4 : 1,
                        type: fatherItem.name,
                        fillColor: sunItem?.fillColor,
                        borderColor: sunItem?.borderColor,
                        showBorder: sunItem?.showBorder,
                        showFill: sunItem?.showFill,
                    })
                    layerNameCount += 1
                })
            })

            // RecordedPoint 处理
            const isRecorded = layerListConfig.find(item => item?.layerSoruceName === 'Recorded')
            if (isRecorded) {
                layerListConfig.push({
                    ...isRecorded,
                    layerSoruceName: 'RecordedPoint',
                    legendName: 'RecordedPoint',
                    layerRenderType: 'shot_circle',
                    layerName: isRecorded.layerName + 1,
                    type: 'Production',
                })
                layerListConfig.forEach(item => {
                    if (item.layerName >= isRecorded.layerName + 1 && item.layerSoruceName !== 'RecordedPoint') {
                        item.layerName += 1
                    }
                })
            }

            setTreeConfig(treeConfig)
            setLayerListConfig(layerListConfig)
        })
    }, [project, allPointTotal])

    if (!project || !rangePicker) return <></>

    return (
        <>
            <Card className='map' ref={mapCard}>
                <div
                    className='switchStyle'
                    onClick={() => {
                        putPositioning({
                            longitude: map.current?.getCenter().lng.toFixed(4),
                            latitude: map.current?.getCenter().lat.toFixed(4),
                            zoom: map.current?.getZoom().toFixed(3),
                            id: JSON.parse(currentViewport).id,
                        })
                            .then(() => {
                                getPositioningList().then(res => setPositioningList(res?.data?.data))
                                message.success(t('Map.tips16'))
                            })
                            .catch(() => message.error(t('Map.tips17')))
                    }}
                >
                    <SaveViewIcon />
                </div>

                {dataLoading &&
                    (downloadedSizeText === '0 KB' ? (
                        <div className='dataLoading'>
                            <SyncOutlined spin />
                        </div>
                    ) : (
                        <div className='dataLoading'>
                            <Tooltip title={dataTooltipText || t('Map.dataLoading')}>
                                <span style={{ display: 'inline-block' }}>
                                    <FlowProgress
                                        percent={loadPercent}
                                        size={25}
                                        strokeWidth={2.5}
                                        color='#4093F9'
                                        speed={0.8}
                                    />
                                </span>
                            </Tooltip>
                        </div>
                    ))}
                {geometryLoading &&
                    (geometryDownloadedRef?.current === 0 ? (
                        <div className='dataLoading'>
                            <SyncOutlined spin />
                        </div>
                    ) : (
                        <div className='dataLoading'>
                            <Tooltip title={geometryTooltipText || t('Map.geometryLoading')}>
                                <span style={{ display: 'inline-block' }}>
                                    <FlowProgress
                                        percent={geometryPercent}
                                        size={25}
                                        strokeWidth={2.5}
                                        color='#F39C12'
                                        speed={0.8}
                                    />
                                </span>
                            </Tooltip>
                        </div>
                    ))}

                <div className={`sidebar-right ${closeSider ? 'sidebar-right-close' : ''}`}>
                    <div
                        className={`sidebar-botton ${closeSider ? 'sidebar-botton-close' : ''}`}
                        onClick={() => setCloseSider(!closeSider)}
                    >
                        {closeSider ? <LeftOutlined /> : <RightOutlined />}
                    </div>
                    <Tree
                        checkable
                        showIcon={true}
                        checkedKeys={renderLayers}
                        onCheck={onCheckTree}
                        treeData={treeConfig}
                        expandedKeys={expandedKeys}
                        onExpand={expandedKeys => setExpandedKeys(expandedKeys)}
                        onSelect={(selectedKeys, e) => {
                            if (selectedKeys.length > 0) {
                                const addKeys = e.node.children ? e.node.children.map(item => item.key) : selectedKeys
                                onCheckTree([...new Set(renderLayers.concat(addKeys))], e, true)
                            } else {
                                const removeKeys = e.node.children
                                    ? e.node.children.map(item => item.key)
                                    : [e.node.key]
                                const filtered = renderLayers.filter(k => !removeKeys.includes(k))
                                onCheckTree(filtered, e, false)
                            }
                        }}
                    />
                </div>

                <div ref={mapContainer} className='map-container' />

                <div className='legend'>
                    {layerListConfig
                        .filter(item => item.layerSoruceName !== 'RecordedPoint' && !item.disableLegend)
                        .map((item, index) => (
                            <div className='legend-item' key={index}>
                                <span
                                    className={`icon ${item.legendBoundaries ? 'icon-boundaries' : ''} ${
                                        item.legendObstacles ? 'icon-obstacles' : ''
                                    }`}
                                    style={{
                                        backgroundColor: item?.fillColor ? item?.fillColor : item.layerColor,
                                        borderColor: item?.borderColor ? item?.borderColor : item.layerColor,
                                    }}
                                />
                                <span className='text'>{item.legendName}</span>
                            </div>
                        ))}
                </div>
            </Card>
        </>
    )
}

export default MapLargeScreen
